import { useEffect, useMemo, useState } from 'react';
import {
  Table,
  Tag,
  Button,
  Space,
  Modal,
  Form,
  Input,
  message,
  Upload,
  Tooltip,
  Popconfirm,
  Typography,
} from 'antd';
import {
  PlusOutlined,
  StopOutlined,
  CheckCircleOutlined,
  EditOutlined,
  UploadOutlined,
  LinkOutlined,
  FilePdfOutlined,
  AppstoreOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import {
  listTenantsAdmin,
  createTenantAdmin,
  setTenantStatus,
  uploadTenantLogo,
} from 'api/tenantsAdmin';
import ColorFieldWithWcag from 'components/admin/tenants/ColorFieldWithWcag';
import TenantConfigDrawer from 'components/admin/TenantConfigDrawer';
import ExecutiveTabsConfig from 'pages/liftlogic/ClientTabsConfig/ExecutiveTabsConfig';
import { apiAssetUrl } from 'utils/apiAssetUrl';

// Página admin STIL para gestionar todos los tenants de la plataforma.
// Protegida por la ruta del router — el endpoint backend además valida
// que el JWT sea USER_LIFTLOGIC.

const STATUS_COLOR = {
  active: 'green',
  suspended: 'orange',
  disabled: 'red',
};

export default function TenantsPage() {
  const [tenants, setTenants] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [tabsOpen, setTabsOpen] = useState(false);
  const [tabsTenant, setTabsTenant] = useState(null);
  const [configOpen, setConfigOpen] = useState(false);
  const [configTenant, setConfigTenant] = useState(null);
  const navigate = useNavigate();

  const refresh = async () => {
    setLoading(true);
    try {
      const data = await listTenantsAdmin();
      setTenants(Array.isArray(data) ? data : []);
    } catch (err) {
      message.error(err?.response?.data?.mensaje || 'No se pudo cargar la lista de tenants');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const onToggleStatus = async (tenant) => {
    const next = tenant.status === 'active' ? 'suspended' : 'active';
    try {
      await setTenantStatus(tenant.id, next);
      message.success(`Tenant ${next === 'active' ? 'activado' : 'suspendido'}`);
      await refresh();
    } catch (err) {
      message.error(err?.response?.data?.mensaje || 'Error al cambiar status');
    }
  };

  const onUploadLogo = async (tenant, file) => {
    try {
      const { logo_url } = await uploadTenantLogo(tenant.id, file);
      message.success('Logo actualizado');
      await refresh();
      return logo_url;
    } catch (err) {
      message.error(err?.response?.data?.mensaje || 'Error al subir el logo');
    }
    return null;
  };

  // Filtro client-side por organización / subdominio / custom domain.
  const filteredTenants = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return tenants;
    return tenants.filter((t) =>
      [t.organization, t.subdomain, t.customDomain].some((v) => (v || '').toLowerCase().includes(q))
    );
  }, [tenants, search]);

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    {
      title: 'Logo',
      dataIndex: 'logo_url',
      width: 80,
      render: (url) =>
        url ? (
          <img
            src={apiAssetUrl(url)}
            alt="logo"
            style={{ height: 32, maxWidth: 80, objectFit: 'contain' }}
          />
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
    },
    { title: 'Organización', dataIndex: 'organization' },
    {
      title: 'Subdominio',
      dataIndex: 'subdomain',
      render: (v) => (v ? <code>{v}</code> : <Typography.Text type="secondary">—</Typography.Text>),
    },
    {
      title: 'Custom Domain',
      dataIndex: 'customDomain',
      render: (v) => (v ? <code>{v}</code> : <Typography.Text type="secondary">—</Typography.Text>),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 110,
      render: (s) => <Tag color={STATUS_COLOR[s] || 'default'}>{s}</Tag>,
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 300,
      render: (_, tenant) => (
        <Space size="small">
          <Tooltip title="Editar">
            <Button
              icon={<EditOutlined />}
              size="small"
              onClick={() =>
                navigate(`/admin/tenants/${tenant.id}/edit`, {
                  state: { tenant },
                })
              }
            />
          </Tooltip>
          <Tooltip title="Plantillas PDF">
            <Button
              icon={<FilePdfOutlined />}
              size="small"
              onClick={() =>
                navigate(`/admin/tenants/${tenant.id}/pdf-templates`, {
                  state: { tenantName: tenant.organization },
                })
              }
            />
          </Tooltip>
          <Tooltip title="Configurar tabs de ejecutivo">
            <Button
              icon={<AppstoreOutlined />}
              size="small"
              onClick={() => {
                setTabsTenant(tenant);
                setTabsOpen(true);
              }}
            />
          </Tooltip>
          <Tooltip title="Configuración por módulo">
            <Button
              icon={<SettingOutlined />}
              size="small"
              onClick={() => {
                setConfigTenant(tenant);
                setConfigOpen(true);
              }}
            />
          </Tooltip>
          <Upload
            accept="image/*"
            showUploadList={false}
            beforeUpload={(file) => {
              onUploadLogo(tenant, file);
              return false;
            }}
          >
            <Tooltip title="Subir logo">
              <Button icon={<UploadOutlined />} size="small" />
            </Tooltip>
          </Upload>
          <Popconfirm
            title={
              tenant.status === 'active'
                ? `Suspender ${tenant.organization}?`
                : `Activar ${tenant.organization}?`
            }
            okText="Sí"
            cancelText="No"
            onConfirm={() => onToggleStatus(tenant)}
          >
            <Tooltip title={tenant.status === 'active' ? 'Suspender' : 'Activar'}>
              <Button
                icon={tenant.status === 'active' ? <StopOutlined /> : <CheckCircleOutlined />}
                size="small"
                danger={tenant.status === 'active'}
              />
            </Tooltip>
          </Popconfirm>
          {tenant.subdomain && (
            <Tooltip title={`Abrir ${tenant.subdomain}`}>
              <Button
                icon={<LinkOutlined />}
                size="small"
                onClick={() => {
                  const url = tenant.customDomain
                    ? `https://${tenant.customDomain}`
                    : `https://${tenant.subdomain}.stiltech.cl`;
                  window.open(url, '_blank');
                }}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Typography.Title level={3} style={{ margin: 0 }}>
          Administración de Tenants
        </Typography.Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
          Crear tenant
        </Button>
      </div>

      <Input.Search
        allowClear
        placeholder="Buscar por organización, subdominio o dominio…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{ width: 360, marginBottom: 16 }}
      />

      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={filteredTenants}
        pagination={{ pageSize: 20 }}
      />

      <CreateTenantModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={async () => {
          setCreateOpen(false);
          await refresh();
        }}
      />

      <ExecutiveTabsConfig
        open={tabsOpen}
        tenant={tabsTenant}
        onClose={() => {
          setTabsOpen(false);
          setTabsTenant(null);
        }}
      />

      <TenantConfigDrawer
        open={configOpen}
        tenant={configTenant}
        onClose={() => {
          setConfigOpen(false);
          setConfigTenant(null);
        }}
        onSaved={refresh}
      />
    </div>
  );
}

function CreateTenantModal({ open, onClose, onCreated }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const payload = {
        organization: values.organization,
        subdomain: values.subdomain,
        customDomain: values.customDomain || null,
        correo: values.correo,
        telefono: values.telefono || null,
        direccion: values.direccion || null,
        web: values.web || null,
        primary_color: values.primary_color,
        secondary_color: values.secondary_color,
        adminUser: {
          rut: values.adminRut,
          fullname: values.adminFullname,
          email: values.adminEmail,
          password: values.adminPassword,
        },
      };
      const data = await createTenantAdmin(payload);
      setResult(data);
      form.resetFields();
      message.success('Tenant creado exitosamente');
    } catch (err) {
      message.error(err?.response?.data?.mensaje || 'Error al crear tenant');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setResult(null);
    onClose();
  };

  return (
    <Modal
      open={open}
      title="Crear nuevo tenant"
      onCancel={handleClose}
      footer={null}
      width={720}
      destroyOnHidden
    >
      {result ? (
        <div>
          <Typography.Paragraph>
            <strong>Tenant creado.</strong> Comparte con el cliente:
          </Typography.Paragraph>
          <Typography.Paragraph copyable>
            <strong>URL:</strong> {result.url}
          </Typography.Paragraph>
          <Typography.Paragraph copyable>
            <strong>Usuario:</strong> {result.adminUser.rut}
          </Typography.Paragraph>
          <Typography.Paragraph copyable>
            <strong>Email:</strong> {result.adminUser.email}
          </Typography.Paragraph>
          <Typography.Paragraph type="warning">
            La contraseña la configuraste al crear el formulario. Se la envías manualmente al
            cliente.
          </Typography.Paragraph>
          <Button
            type="primary"
            onClick={async () => {
              setResult(null);
              await onCreated();
            }}
          >
            Aceptar
          </Button>
        </div>
      ) : (
        <Form
          layout="vertical"
          form={form}
          onFinish={onFinish}
          initialValues={{
            primary_color: '#0055b3',
            secondary_color: '#c5561d',
          }}
        >
          <Typography.Title level={5}>Tenant</Typography.Title>
          <Form.Item label="Organización" name="organization" rules={[{ required: true }]}>
            <Input placeholder="Acme SpA" />
          </Form.Item>
          <Form.Item
            label="Subdominio"
            name="subdomain"
            rules={[
              { required: true },
              {
                pattern: /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/,
                message:
                  'Solo letras minúsculas, números y guión. No puede empezar/terminar con guión.',
              },
              { min: 2, max: 63 },
            ]}
            extra="Se accede vía {subdominio}.stiltech.cl"
          >
            <Input placeholder="acme" />
          </Form.Item>
          <Form.Item label="Custom Domain (opcional)" name="customDomain">
            <Input placeholder="acme.cl" />
          </Form.Item>
          <Form.Item
            label="Email de contacto"
            name="correo"
            rules={[{ required: true, type: 'email' }]}
          >
            <Input placeholder="contacto@acme.cl" />
          </Form.Item>
          <Form.Item label="Teléfono" name="telefono">
            <Input />
          </Form.Item>
          <Form.Item label="Dirección" name="direccion">
            <Input />
          </Form.Item>
          <Form.Item label="Web" name="web">
            <Input placeholder="www.acme.cl" />
          </Form.Item>

          <Typography.Title level={5}>Colores de marca</Typography.Title>
          <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
            Se aplican al dashboard y PDFs del tenant. Deben cumplir WCAG AA (contraste ≥ 4.5:1 con
            el texto auto-calculado).
          </Typography.Paragraph>
          <ColorFieldWithWcag name="primary_color" label="Color primario" />
          <ColorFieldWithWcag name="secondary_color" label="Color secundario" />

          <Typography.Title level={5}>Usuario administrador</Typography.Title>
          <Form.Item label="RUT" name="adminRut" rules={[{ required: true }]}>
            <Input placeholder="12345678-9" />
          </Form.Item>
          <Form.Item label="Nombre completo" name="adminFullname" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label="Email" name="adminEmail" rules={[{ required: true, type: 'email' }]}>
            <Input />
          </Form.Item>
          <Form.Item
            label="Contraseña inicial"
            name="adminPassword"
            rules={[{ required: true, min: 6 }]}
          >
            <Input.Password />
          </Form.Item>

          <Button type="primary" htmlType="submit" loading={loading} block>
            Crear tenant
          </Button>
        </Form>
      )}
    </Modal>
  );
}
